import ApiClient from "./apiClient";
import { QueriesApi } from "./apiTypes";

const projectService = {
  // List and retrieve
  getProjectList: (queries: QueriesApi) => ApiClient.get('openstack/projects', {
    params: {
      page_size: queries.pageSize,
      search: queries.search,
      ordering: queries.ordering,
      page: queries.page,
      disabled: queries.disabled,
      is_domain: queries.is_domain,
      services_count: queries.services_count,
      filtering: queries.filtering,
    }
  }),

  getProjectDetail: (projectId: string | number) => ApiClient.get(`openstack/projects/${projectId}`),

  // Create, update, delete
  createProject: (data: {
    name: string;
    description: string;
    disabled?: boolean;
  }) => ApiClient.post('openstack/projects', data),

  updateProject: (projectId: string | number, data: {
    name?: string;
    description?: string;
    disabled?: boolean;
  }) => ApiClient.put(`openstack/projects/${projectId}`, data),

  deleteProject: (projectId: string | number, deleteAllResources: boolean = false) =>
    ApiClient.delete(`openstack/projects/${projectId}`, {
      params: { delete_all_resources: deleteAllResources }
    }),

  // Compute quotas
  getComputeQuotas: (projectId: string | number, regionId: string = 'default') => {
    return ApiClient.get(`openstack/projects/${projectId}/get_compute_quotas`, {
      params: { region_id: regionId }
    });
  },

  setComputeQuotas: (projectId: string | number, data: {
    region_id?: string;
    force?: boolean;
    set_for_all_regions?: boolean;
    instances?: number;
    cores?: number;
    key_pairs?: number;
    metadata_items?: number;
    ram?: number;
    server_groups?: number;
    server_group_members?: number;
  }) => ApiClient.post(`openstack/projects/${projectId}/set_compute_quotas`, data),

  // Volume quotas
  getVolumeQuotas: (projectId: string | number, regionId: string = 'default') => {
    return ApiClient.get(`openstack/projects/${projectId}/get_volume_quotas`, {
      params: { region_id: regionId }
    });
  },

  setVolumeQuotas: (projectId: string | number, data: {
    region_id?: string;
    force?: boolean;
    set_for_all_regions?: boolean;
    volumes?: number;
    snapshots?: number;
    backups?: number;
    groups?: number;
    per_volume_gigabytes?: number;
    gigabytes?: number;
    backup_gigabytes?: number;
  }) => ApiClient.post(`openstack/projects/${projectId}/set_volume_quotas`, data),

  // Network quotas
  getNetworkQuotas: (projectId: string | number, regionId: string = 'default') => {
    return ApiClient.get(`openstack/projects/${projectId}/get_network_quotas`, {
      params: { region_id: regionId }
    });
  },

  setNetworkQuotas: (projectId: string | number, data: {
    region_id?: string;
    force?: boolean;
    set_for_all_regions?: boolean;
    floatingip?: number;
    network?: number;
    port?: number;
    rbac_policy?: number;
    router?: number;
    security_group?: number;
    security_group_rule?: number;
    subnet?: number;
    subnetpool?: number;
  }) => ApiClient.post(`openstack/projects/${projectId}/set_network_quotas`, data),

  // Database/Trove quotas
  getDatabaseQuotas: (projectId: string | number, regionId: string = 'default') => {
    return ApiClient.get(`openstack/projects/${projectId}/get_database_quotas`, {
      params: { region_id: regionId }
    });
  },

  setDatabaseQuotas: (projectId: string | number, data: {
    region_id?: string;
    force?: boolean;
    set_for_all_regions?: boolean;
    instances?: number;
    ram?: number;
    backups?: number;
    volumes?: number;
    [key: string]: any;
  }) => ApiClient.post(`openstack/projects/${projectId}/set_database_quotas`, data),

  // Get available regions for quotas
  getRegions: () => ApiClient.get('openstack/regions'),

  // Get available roles
  getAvailableRoles: (projectId: string | number) =>
    ApiClient.get(`openstack/projects/${projectId}/get_available_roles`),

  // Get role assignments
  getRoleAssignments: (projectId: string | number) =>
    ApiClient.get(`openstack/projects/${projectId}/get_role_assignments`),

  // Update role assignments
  updateRoleAssignments: (projectId: string | number, data: any) =>
    ApiClient.put(`openstack/projects/${projectId}/update_role_assignments`, data),
};

export default projectService;
